/**
 * TeamView - Composant de gestion d'équipe
 * Permet aux propriétaires avec un abonnement d'inviter et gérer des membres
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Users, Mail, Trash2, UserPlus, Crown, LogOut, Loader, AlertCircle, CheckCircle, ArrowLeft, Clock } from 'lucide-react';
import config from '../config';

function TeamView({ token, user, userSubscription, onBack }) {
  const [loading, setLoading] = useState(true);
  const [teamInfo, setTeamInfo] = useState(null);
  const [teamMembers, setTeamMembers] = useState(null);
  const [canInviteInfo, setCanInviteInfo] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Charger les informations d'équipe
  const loadTeamData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Charger les infos équipe (pour savoir si membre ou propriétaire)
      const infoResponse = await fetch(`${config.API_URL}/api/invitations/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const infoData = await infoResponse.json();

      if (infoData.success) {
        setTeamInfo(infoData);
      }

      // Si l'utilisateur est propriétaire (pas membre d'une autre équipe)
      if (!infoData.isTeamMember) {
        // Charger la capacité d'invitation
        const canInviteResponse = await fetch(`${config.API_URL}/api/invitations/can-invite`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const canInviteData = await canInviteResponse.json();
        if (canInviteData.success) {
          setCanInviteInfo(canInviteData);
        }

        // Charger les membres de l'équipe
        const teamResponse = await fetch(`${config.API_URL}/api/invitations/team`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const teamData = await teamResponse.json();
        if (teamData.success) {
          setTeamMembers(teamData);
        }
      }
    } catch (err) {
      console.error('Erreur chargement équipe:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadTeamData();
  }, [loadTeamData]);

  // Envoyer une invitation
  const handleSendInvitation = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${config.API_URL}/api/invitations/send`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: inviteEmail.trim() })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Invitation envoyée à ${inviteEmail}`);
        setInviteEmail('');
        loadTeamData();
      } else {
        setError(data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  // Annuler une invitation
  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Annuler cette invitation ?')) return;

    try {
      const response = await fetch(`${config.API_URL}/api/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Invitation annulée');
        loadTeamData();
      } else {
        setError(data.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  // Retirer un membre
  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Retirer ${memberName} de l'équipe ? Cette personne perdra l'accès aux données partagées et son abonnement passera en Solo.`)) return;

    try {
      const response = await fetch(`${config.API_URL}/api/invitations/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`${memberName} a été retiré de l'équipe`);
        loadTeamData();
      } else {
        setError(data.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  // Quitter une équipe (pour un membre)
  const handleLeaveTeam = async () => {
    if (!window.confirm('Quitter cette équipe ? Vous perdrez l\'accès aux données partagées et votre abonnement passera en Solo.')) return;

    try {
      const response = await fetch(`${config.API_URL}/api/invitations/leave`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Vous avez quitté l\'équipe');
        // Recharger pour mettre à jour l'UI
        window.location.reload();
      } else {
        setError(data.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  // Formater la date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Formater la date relative (il y a X jours)
  const formatRelativeDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Expiré';
    if (diffDays === 0) return "Expire aujourd'hui";
    if (diffDays === 1) return 'Expire demain';
    return `Expire dans ${diffDays} jours`;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 60px', textAlign: 'center' }}>
        <Loader className="spin" size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '16px', color: '#6b7280' }}>Chargement...</p>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin { animation: spin 1s linear infinite; }
        `}</style>
      </div>
    );
  }

  // Vue pour un membre d'équipe
  if (teamInfo?.isTeamMember) {
    return (
      <div style={{ padding: '40px 60px' }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            color: '#374151',
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          <ArrowLeft size={16} />
          Retour
        </button>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '30px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '15px'
        }}>
          <Users size={28} style={{ marginRight: '12px', color: '#1a1a1a' }} />
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
            Mon Équipe
          </h1>
        </div>

        {/* Info membre */}
        <div style={{
          backgroundColor: '#FAF9F5',
          border: '1px solid #bfdbfe',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Crown size={24} color="white" />
            </div>
            <div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>Propriétaire de l'équipe</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                {teamInfo.teamOwner?.name || teamInfo.teamOwner?.email}
              </div>
              {teamInfo.teamOwner?.name && (
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  {teamInfo.teamOwner.email}
                </div>
              )}
            </div>
          </div>

          <p style={{ margin: '0 0 16px', color: '#374151', lineHeight: '1.6' }}>
            Vous êtes membre de cette équipe. Vous bénéficiez du plan <strong>{userSubscription?.plan}</strong> et
            avez accès aux données partagées (connecteurs, sites, bibliothèque).
          </p>

          <button
            onClick={handleLeaveTeam}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: 'white',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <LogOut size={16} />
            Quitter l'équipe
          </button>
        </div>

        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '16px'
          }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {success && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#16a34a',
            marginBottom: '16px'
          }}>
            <CheckCircle size={16} />
            {success}
          </div>
        )}
      </div>
    );
  }

  // Vue pour un utilisateur qui ne peut pas inviter (CLIENT)
  if (canInviteInfo && !canInviteInfo.canInvite && canInviteInfo.upgradeRequired) {
    return (
      <div style={{ padding: '40px 60px' }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            color: '#374151',
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          <ArrowLeft size={16} />
          Retour
        </button>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '30px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '15px'
        }}>
          <Users size={28} style={{ marginRight: '12px', color: '#1a1a1a' }} />
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
            Équipe
          </h1>
        </div>

        <div style={{
          backgroundColor: '#fefce8',
          border: '1px solid #fde047',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center'
        }}>
          <UserPlus size={48} style={{ color: '#ca8a04', marginBottom: '16px' }} />
          <h2 style={{ margin: '0 0 12px', fontSize: '20px', color: '#854d0e' }}>
            Fonctionnalité réservée aux abonnés
          </h2>
          <p style={{ margin: '0 0 24px', color: '#713f12', lineHeight: '1.6' }}>
            Choisissez un plan pour inviter des membres dans votre équipe.
            Les membres invités bénéficieront de votre plan et auront accès à vos données partagées.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <div style={{
              padding: '16px 24px',
              backgroundColor: 'white',
              border: '2px solid #c2410c',
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: '600', color: '#c2410c', marginBottom: '4px' }}>Solo</div>
              <div style={{ color: '#6b7280', fontSize: '14px' }}>1 membre</div>
            </div>
            <div style={{
              padding: '16px 24px',
              backgroundColor: 'white',
              border: '2px solid #059669',
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: '600', color: '#059669', marginBottom: '4px' }}>Team</div>
              <div style={{ color: '#6b7280', fontSize: '14px' }}>3 membres max</div>
            </div>
            <div style={{
              padding: '16px 24px',
              backgroundColor: 'white',
              border: '2px solid #2563eb',
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: '600', color: '#2563eb', marginBottom: '4px' }}>Agency</div>
              <div style={{ color: '#6b7280', fontSize: '14px' }}>10 membres max</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vue propriétaire (peut inviter)
  const plan = userSubscription?.plan || 'SOLO';
  const isAdmin = user?.role === 'ADMIN';
  const currentCount = teamMembers?.currentCount || 0;
  const pendingCount = teamMembers?.pendingCount || 0;
  const limit = teamMembers?.limit || 0;

  return (
    <div style={{ padding: '40px 60px' }}>
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: 'transparent',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          color: '#374151',
          fontSize: '14px',
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        <ArrowLeft size={16} />
        Retour
      </button>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '30px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '15px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Users size={28} style={{ marginRight: '12px', color: '#1a1a1a' }} />
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
            Mon Équipe
          </h1>
        </div>

        {/* Compteur */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: '#f3f4f6',
          borderRadius: '8px'
        }}>
          <Users size={18} color="#6b7280" />
          <span style={{ fontWeight: '600', color: '#374151' }}>
            {currentCount} / {limit}
          </span>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            membre{currentCount !== 1 ? 's' : ''}
          </span>
          {pendingCount > 0 && (
            <span style={{ color: '#f59e0b', fontSize: '14px' }}>
              (+{pendingCount} en attente)
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '16px'
        }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {success && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          color: '#16a34a',
          marginBottom: '16px'
        }}>
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      {/* Formulaire d'invitation */}
      {canInviteInfo?.canInvite && (
        <div style={{
          backgroundColor: '#FAF9F5',
          border: '1px solid #e1e8ed',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h2 style={{ margin: '0 0 16px', fontSize: '18px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserPlus size={20} />
            Inviter un nouveau membre
          </h2>

          <form onSubmit={handleSendInvitation} style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <input
                type="email"
                placeholder="Adresse email..."
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={sending}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '12px 44px 12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
            <button
              type="submit"
              disabled={sending || !inviteEmail.trim()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                backgroundColor: sending || !inviteEmail.trim() ? '#9ca3af' : '#1a1a1a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: sending || !inviteEmail.trim() ? 'not-allowed' : 'pointer'
              }}
            >
              {sending ? (
                <>
                  <Loader size={16} className="spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Mail size={16} />
                  Envoyer l'invitation
                </>
              )}
            </button>
          </form>

          <p style={{ margin: '12px 0 0', color: '#6b7280', fontSize: '13px' }}>
            Le membre recevra un email avec un lien pour rejoindre votre équipe.
            Il bénéficiera de votre plan {plan} et aura accès à vos données partagées.
          </p>
        </div>
      )}

      {/* Liste des membres */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        marginBottom: '24px'
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#FAF9F5'
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', color: '#374151' }}>
            Membres de l'équipe ({currentCount})
          </h3>
        </div>

        {(!teamMembers?.members || teamMembers.members.length === 0) ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <Users size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>Aucun membre dans votre équipe pour le moment</p>
            <p style={{ margin: '8px 0 0', fontSize: '14px' }}>
              Invitez des collaborateurs pour partager vos données
            </p>
          </div>
        ) : (
          <div>
            {teamMembers.members.map((member) => (
              <div
                key={member.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {member.picture ? (
                    <img
                      src={member.picture}
                      alt={member.name}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#6b7280'
                    }}>
                      {(member.name || member.email).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>
                      {member.name || member.email}
                    </div>
                    {member.name && (
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>
                        {member.email}
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                      Membre depuis le {formatDate(member.createdAt)}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleRemoveMember(member.id, member.name || member.email)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #fca5a5',
                    borderRadius: '6px',
                    color: '#dc2626',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <Trash2 size={14} />
                  Retirer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invitations en attente */}
      {teamMembers?.pendingInvitations && teamMembers.pendingInvitations.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#fffbeb'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={18} />
              Invitations en attente ({teamMembers.pendingInvitations.length})
            </h3>
          </div>

          <div>
            {teamMembers.pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#fef3c7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Mail size={18} color="#d97706" />
                  </div>
                  <div>
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>
                      {invitation.email}
                    </div>
                    <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '2px' }}>
                      {formatRelativeDate(invitation.expiresAt)}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleCancelInvitation(invitation.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#6b7280',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <Trash2 size={14} />
                  Annuler
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamView;
